import { Button, ErrorMessage, notify, Skeleton } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { AccessTokenService, ServerService } from '@affine/core/modules/cloud';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { IntegrationSettingHeader } from '../setting';
import MCPIcon from './MCP.inline.svg';
import * as styles from './setting-panel.css';

export const McpServerSettingPanel = () => {
  return <McpServerSetting />;
};

const McpServerSettingHeader = ({ action }: { action?: ReactNode }) => {
  const t = useI18n();

  return (
    <IntegrationSettingHeader
      icon={<img src={MCPIcon} />}
      name={t['com.affine.integration.mcp-server.name']()}
      desc={t['com.affine.integration.mcp-server.desc']()}
      action={action}
    />
  );
};

const McpServerSetting = () => {
  const serverService = useService(ServerService);
  const accessTokenService = useService(AccessTokenService);
  const accessTokens = useLiveData(accessTokenService.accessTokens$);
  const isRevalidating = useLiveData(accessTokenService.isRevalidating$);
  const error = useLiveData(accessTokenService.error$);
  const [mutating, setMutating] = useState(false);
  const t = useI18n();

  const mcpAccessToken = useMemo(() => {
    return accessTokens?.find(token => token.name === 'mcp');
  }, [accessTokens]);

  // Generate config for user-level MCP endpoint (single entry for all workspaces)
  const code = useMemo(() => {
    if (!mcpAccessToken) {
      return null;
    }

    return JSON.stringify(
      {
        mcpServers: {
          'yoDEV Workplace': {
            type: 'http',
            url: `${serverService.server.baseUrl}/api/user/mcp`,
            note: 'Read, search, create, and edit docs across all your yoDEV workspaces',
            headers: {
              Authorization: `Bearer ${mcpAccessToken.token}`,
            },
          },
        },
      },
      null,
      2
    );
  }, [mcpAccessToken, serverService]);

  const showLoading = accessTokens === null && isRevalidating;
  const showError = accessTokens === null && error !== null;

  useEffect(() => {
    accessTokenService.revalidate();
  }, [accessTokenService]);

  const handleGenerateAccessToken = useAsyncCallback(async () => {
    setMutating(true);
    try {
      if (mcpAccessToken) {
        await accessTokenService.revokeUserAccessToken(mcpAccessToken.id);
      }
      await accessTokenService.generateUserAccessToken('mcp');
    } catch (err) {
      notify.error({
        error: UserFriendlyError.fromAny(err),
      });
    } finally {
      setMutating(false);
    }
  }, [accessTokenService, mcpAccessToken]);

  const handleRevokeAccessToken = useAsyncCallback(async () => {
    setMutating(true);
    try {
      if (mcpAccessToken) {
        await accessTokenService.revokeUserAccessToken(mcpAccessToken.id);
      }
    } catch (err) {
      notify.error({
        error: UserFriendlyError.fromAny(err),
      });
    } finally {
      setMutating(false);
    }
  }, [accessTokenService, mcpAccessToken]);

  if (showLoading) {
    return (
      <div>
        <McpServerSettingHeader />
        <Skeleton />
      </div>
    );
  }

  if (showError) {
    return (
      <div>
        <McpServerSettingHeader />
        <ErrorMessage>{error}</ErrorMessage>
      </div>
    );
  }

  return (
    <div>
      <McpServerSettingHeader />

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Personal access token</div>
          {!mcpAccessToken ? (
            <Button
              variant="primary"
              onClick={handleGenerateAccessToken}
              disabled={mutating}
            >
              Create New
            </Button>
          ) : (
            <Button
              variant="error"
              onClick={handleRevokeAccessToken}
              disabled={mutating}
            >
              Delete
            </Button>
          )}
        </div>
        <p className={styles.sectionDescription}>
          This access token allows Claude Code to read, search, create, and edit
          documents across all your workspaces. Keep this information secure.
          Deleting it will invalidate the access token.
        </p>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Server Config</div>
          <Button
            variant="primary"
            onClick={() => {
              if (!code) return;
              navigator.clipboard.writeText(code);
              notify.success({
                title: t['Copied to clipboard'](),
              });
            }}
            disabled={!code || mutating}
          >
            Copy json
          </Button>
        </div>
        {code ? (
          <pre className={styles.preArea}>{code}</pre>
        ) : (
          <p
            className={styles.sectionDescription}
            style={{ textAlign: 'center' }}
          >
            No access token found, please generate one first.
          </p>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>Available Tools</div>
        </div>
        <br />

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>list_workspaces</div>
          </div>
          <div className={styles.sectionDescription}>
            List all workspaces you have access to. Returns workspace IDs and
            names for use with other tools.
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>list_documents</div>
          </div>
          <div className={styles.sectionDescription}>
            List all documents in a workspace with their IDs, titles, and
            metadata. Use this to browse available documents before reading
            specific ones.
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>read_document</div>
          </div>
          <div className={styles.sectionDescription}>
            Return the complete markdown content of a document by its ID. Use
            this when you need the full content of a specific document.
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>keyword_search</div>
          </div>
          <div className={styles.sectionDescription}>
            Fuzzy search workspace documents for keywords or phrases and return
            matching documents ranked by textual match. Use this for
            straightforward term-based lookups.
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>semantic_search</div>
          </div>
          <div className={styles.sectionDescription}>
            Find conceptually related passages using AI-powered semantic
            similarity search. Use this when keyword search fails or you need
            meaning-level matches (paraphrases, synonyms, related concepts).
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>create_document</div>
          </div>
          <div className={styles.sectionDescription}>
            Create a new document in a workspace from markdown content. Provide
            a title and markdown body to generate a new document.
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>update_document_content</div>
          </div>
          <div className={styles.sectionDescription}>
            Replace the entire content of an existing document with new markdown
            content. Use this for major document rewrites.
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>append_to_document</div>
          </div>
          <div className={styles.sectionDescription}>
            Append markdown content to the end of an existing document. Useful
            for adding notes, sections, or updates without replacing existing
            content.
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>update_document_title</div>
          </div>
          <div className={styles.sectionDescription}>
            Update the title of an existing document without changing its
            content.
          </div>
        </div>
      </div>
    </div>
  );
};
